import React, { useEffect, useState } from 'react';
import CustomDatePicker from './CustomDatePicker.js';
import {
  draftFromOutOfOffice,
  payloadFromDraft,
  tomorrowCalendarDate,
} from '../utils/outOfOffice.js';
import './OutOfOffice.css';

function draftKey(draft) {
  if (!draft.away) {
    return 'in';
  }
  if (draft.mode === 'indefinite') {
    return 'indefinite';
  }
  return `until:${draft.date}`;
}

const OutOfOfficeControl = ({
  outOfOffice = null,
  onSave,
  onCancel,
  disabled = false,
  variant = 'inline',
}) => {
  const initial = draftFromOutOfOffice(outOfOffice);
  const incomingKey = draftKey(initial);
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setDraft(draftFromOutOfOffice(outOfOffice));
    setError(null);
    // incomingKey already encodes the stored status, so a new object with the same value does not wipe an edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingKey]);

  const isModal = variant === 'modal';
  const changed = draftKey(draft) !== draftKey(initial);
  const dateMissing = draft.away && draft.mode === 'until' && !draft.date;
  const canSave = changed && !dateMissing && !saving && !disabled;
  const showActions = isModal || changed || Boolean(onCancel);

  const handleSave = async () => {
    if (!canSave) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(payloadFromDraft(draft));
    } catch (saveError) {
      console.error('Failed to update availability:', saveError);
      setError('Could not update availability');
    } finally {
      setSaving(false);
    }
  };

  const handleDismiss = () => {
    setDraft(initial);
    setError(null);
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <div className={`ooo-control${isModal ? ' ooo-control--modal' : ''}`}>
      <div className="ooo-segment" role="group" aria-label="Availability">
        <button
          type="button"
          className={!draft.away ? 'is-selected' : ''}
          aria-pressed={!draft.away}
          disabled={disabled || saving}
          onClick={() => setDraft((current) => ({ ...current, away: false }))}
        >
          In office
        </button>
        <button
          type="button"
          className={draft.away ? 'is-selected is-away' : ''}
          aria-pressed={draft.away}
          disabled={disabled || saving}
          onClick={() => setDraft((current) => (
            current.away ? current : { ...current, away: true, mode: 'until' }
          ))}
        >
          Out of office
        </button>
      </div>

      {draft.away && (
        <div className="ooo-return">
          {draft.mode === 'until' && (
            <>
              <span className="ooo-return-label">Back on</span>
              <CustomDatePicker
                value={draft.date}
                min={tomorrowCalendarDate()}
                allowFuture
                onChange={(date) => setDraft((current) => ({ ...current, date }))}
              />
              <p className="ooo-hint">Treated as back from the morning of this day.</p>
            </>
          )}
          <label className={`ooo-checkbox-row${disabled || saving ? ' is-disabled' : ''}`}>
            <input
              type="checkbox"
              className="ooo-checkbox"
              checked={draft.mode === 'indefinite'}
              disabled={disabled || saving}
              onChange={(event) => setDraft((current) => ({
                ...current,
                mode: event.target.checked ? 'indefinite' : 'until',
              }))}
            />
            <span>No return date</span>
          </label>
          {draft.mode === 'indefinite' && (
            <p className="ooo-hint">Stays out of office until this is turned off.</p>
          )}
        </div>
      )}

      {error && <p className="ooo-control-error">{error}</p>}

      {showActions && (
        <div className="ooo-actions">
          <button
            type="button"
            className="ooo-dismiss"
            onClick={handleDismiss}
            disabled={saving}
          >
            {isModal || changed ? 'Cancel' : 'Close'}
          </button>
          {(isModal || changed) && (
            <button
              type="button"
              className="ooo-save"
              onClick={handleSave}
              disabled={!canSave}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default OutOfOfficeControl;
